import {
  Autocomplete,
  AutocompleteValue,
  Box,
  MenuList,
  TextField,
  TextFieldProps,
} from "@mui/material";
import useCreation from "ahooks/es/useCreation";
import useMemoizedFn from "ahooks/es/useMemoizedFn";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  MemberChip,
  MemberMenuItemContent,
} from "../../../../../../../../../global/components";
import { StyledPopper } from "../../../../../../../../../global/styles";
import { stripDiacritics } from "../../../../../../../../../modules/assets/helperFunctions";
import { GroupMember } from "../../../../../../domain";
import { useDedicatedOptions } from "../../../../../../hooks";
import { createDedicatedSearchFuse } from "./search";
import { SelectedMemberAvatar } from "./SelectedMemberAvatar";

const MIN_CHARS_TO_SEARCH = 2;
const CHUNK_SIZE = 50;

export type DedicatedAutocompleteProps = Omit<
  TextFieldProps,
  "value" | "onChange"
> & {
  multiple: boolean;
  value: GroupMember[];
  onDedicatedChange: (newValues: {
    memberships: GroupMember[];
    teamIds: string[];
  }) => void;
  showTeamAsClickable?: boolean;
  selectedTeamIds?: string[];
};

export interface TeamOption {
  id: string;
  isTeam: true;
  groupName: string;
  teamName: string;
  members: GroupMember[];
  user: {
    name: { full: string };
    avatarImageUrlSrc: null;
  };
}

export interface SelectedTeam {
  teamName: string;
  members: GroupMember[];
}

const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement> & { capacityReached?: boolean }
>(function (props, ref) {
  const { children, capacityReached, ...other } = props;
  return (
    <MenuList
      component="div"
      ref={ref}
      {...other}
      sx={{ position: "relative", padding: 1 }}
    >
      {children}
    </MenuList>
  );
});

export const DedicatedAutocomplete: React.FC<DedicatedAutocompleteProps> = ({
  value,
  onDedicatedChange,
  multiple,
  showTeamAsClickable = false,
  selectedTeamIds = [],
  ...props
}) => {
  const { t } = useTranslation("asset-management");
  const baseOptions: GroupMember[] = useDedicatedOptions(value);

  React.useEffect(() => {
    if (!showTeamAsClickable || selectedTeamIds.length === 0) return;
    const valueIds = new Set(value?.map((v) => v.id) || []);
    const missing = baseOptions
      .filter((m) => selectedTeamIds.includes(m.groupId!))
      .filter((m) => !valueIds.has(m.id));

    if (missing.length > 0) {
      onDedicatedChange({
        memberships: [...value, ...missing],
        teamIds: selectedTeamIds,
      });
    }
  }, [
    selectedTeamIds,
    baseOptions,
    onDedicatedChange,
    showTeamAsClickable,
    value,
  ]);

  const addMissing = (members: GroupMember[]) => {
    const merged = [...value];
    members.forEach((m) => {
      if (!merged.some((x) => x.id === m.id)) merged.push(m);
    });
    return merged;
  };

  const removeMembers = (members: GroupMember[]) =>
    value?.filter((m) => !members.some((rm) => rm.id === m.id));

  const handleChange = useMemoizedFn(
    (
      event: React.SyntheticEvent,
      newValue: AutocompleteValue<
        GroupMember | TeamOption,
        any,
        unknown,
        unknown
      >
    ) => {
      event.stopPropagation();
      if (!Array.isArray(newValue)) return;
      const currentMemberships = value;
      const currentTeamIds = selectedTeamIds;
      const isTeamOption = (
        item: GroupMember | TeamOption
      ): item is TeamOption => "isTeam" in item && item.isTeam === true;
      const teamItem = newValue.find(isTeamOption);
      if (teamItem) {
        const { teamName: teamId, members } = teamItem;
        let newTeamIds: string[];
        let newMemberships: GroupMember[];
        if (currentTeamIds.includes(teamId)) {
          newTeamIds = currentTeamIds.filter((id) => id !== teamId);
          newMemberships = removeMembers(members);
        } else {
          newTeamIds = [...currentTeamIds, teamId];
          newMemberships = addMissing(members);
        }
        onDedicatedChange({ memberships: newMemberships, teamIds: newTeamIds });
        return;
      }
      const addedMember = newValue.find(
        (it) => !currentMemberships.some((m) => m.id === (it as GroupMember).id)
      ) as GroupMember | undefined;
      const removedMember = currentMemberships.find(
        (m) => !newValue.some((it) => (it as GroupMember).id === m.id)
      );
      if (addedMember) {
        const afterAdd = [...currentMemberships, addedMember];
        onDedicatedChange({
          memberships: afterAdd,
          teamIds: currentTeamIds,
        });
        return;
      }
      if (removedMember) {
        const teamId = removedMember.groupId as string | undefined;
        let newTeamIds = currentTeamIds;
        if (teamId && currentTeamIds.includes(teamId)) {
          newTeamIds = currentTeamIds.filter((id) => id !== teamId);
        }
        const afterRemove = currentMemberships.filter(
          (m) => m.id !== removedMember.id
        );
        onDedicatedChange({ memberships: afterRemove, teamIds: newTeamIds });
      }
    }
  );

  const [currentInput, setCurrentInput] = React.useState("");
  const deferredInput = React.useDeferredValue(currentInput);

  const [visibleCount, setVisibleCount] = React.useState(CHUNK_SIZE);

  const allOptions: (GroupMember | TeamOption)[] = useCreation(() => {
    if (!showTeamAsClickable) return baseOptions;
    const groupedMembers = baseOptions.reduce((acc, member) => {
      const teamName = member.groupName;
      if (!acc[teamName]) acc[teamName] = [];
      acc[teamName].push(member);
      return acc;
    }, {} as Record<string, GroupMember[]>);
    const sortedEntries = Object.entries(groupedMembers).sort(
      ([, membersA], [, membersB]) => {
        const aHasNull = membersA.some((m) => m.groupId == null);
        const bHasNull = membersB.some((m) => m.groupId == null);
        if (aHasNull === bHasNull) return 0;
        if (aHasNull) return 1;
        return -1;
      }
    );
    const enhanced: (GroupMember | TeamOption)[] = [];
    sortedEntries.forEach(([teamName, members]) => {
      const allHaveIds = members.every((m) => m.groupId != null);
      if (allHaveIds) {
        const teamId = members[0].groupId as string;
        enhanced.push({
          id: `team-${teamId}`,
          isTeam: true,
          groupName: teamName,
          teamName: teamId,
          members,
          user: { name: { full: "Ceo tim" }, avatarImageUrlSrc: null },
        } as TeamOption);
      }
      enhanced.push(...members);
    });
    return enhanced;
  }, [baseOptions, showTeamAsClickable]);

  const fuse = useCreation(
    () => createDedicatedSearchFuse(allOptions),
    [allOptions]
  );

  React.useEffect(() => {
    setVisibleCount(CHUNK_SIZE);
  }, [deferredInput]);

  const filteredOptionsAll = React.useMemo(() => {
    const raw = deferredInput.trim();
    if (raw.length < MIN_CHARS_TO_SEARCH) {
      return allOptions;
    }
    const query = stripDiacritics(raw);
    const result = fuse.search(query);
    return result.map((it) => it.item);
  }, [deferredInput, allOptions, fuse]);

  const visibleOptions = React.useMemo(
    () => filteredOptionsAll.slice(0, visibleCount),
    [filteredOptionsAll, visibleCount]
  );

  const handleListboxScroll = useMemoizedFn(
    (e: React.UIEvent<HTMLUListElement>) => {
      const el = e.currentTarget;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
      if (nearBottom && visibleOptions.length < filteredOptionsAll.length) {
        setVisibleCount((prev) =>
          Math.min(prev + CHUNK_SIZE, filteredOptionsAll.length)
        );
      }
    }
  );

  const displayTags = useMemo(() => {
    if (!showTeamAsClickable) return value;
    const idToGroupName = new Map<string, string>();
    allOptions.forEach((opt) => {
      if ("isTeam" in opt && opt.isTeam) {
        idToGroupName.set(opt.teamName, opt.groupName);
      } else {
        const m = opt as GroupMember;
        if (m.groupId) idToGroupName.set(m.groupId, m.groupName);
      }
    });
    const teamChips: SelectedTeam[] = selectedTeamIds.map((teamId) => ({
      teamName: idToGroupName.get(teamId) ?? teamId,
      members: value?.filter((m) => m.groupId === teamId),
    }));
    const individualChips =
      value?.filter((m) => !selectedTeamIds.includes(m.groupId as string)) ??
      [];
    return [...teamChips, ...individualChips];
  }, [value, selectedTeamIds, showTeamAsClickable, allOptions]);

  const removeLastTagIfNeeded = (e: React.KeyboardEvent<Element>): boolean => {
    if (e.key !== "Backspace" && e.key !== "Delete") return false;
    const input = e.target as HTMLInputElement;
    if (input.value) return false;
    if (displayTags.length === 0) return false;
    e.preventDefault();
    e.stopPropagation();
    const last = displayTags[displayTags.length - 1];
    if ("id" in last) {
      onDedicatedChange({
        memberships: value?.filter((m) => m.id !== last.id),
        teamIds: selectedTeamIds,
      });
    } else {
      const teamId = last.members[0]?.groupId;
      if (!teamId) return false;
      onDedicatedChange({
        memberships: value?.filter((m) => m.groupId !== teamId),
        teamIds: selectedTeamIds.filter((id) => id !== teamId),
      });
    }
    return true;
  };
  const handleTeamDelete = useMemoizedFn((item: SelectedTeam) => {
    const filteredMemberships =
      value?.filter((m) => !item.members.some((tm) => tm.id === m.id)) ?? [];
    const teamId = item.members[0]?.groupId ?? "";
    const filteredTeamIds = selectedTeamIds.filter((id) => id !== teamId);
    onDedicatedChange({
      memberships: filteredMemberships,
      teamIds: filteredTeamIds,
    });
  });
  const handleMemberDelete = useMemoizedFn((memberId: string) => {
    const filteredMemberships = value?.filter((m) => m.id !== memberId) ?? [];
    onDedicatedChange({
      memberships: filteredMemberships,
      teamIds: selectedTeamIds,
    });
  });
  const renderTeamTag = (item: SelectedTeam, tagProps: any, key: number) => (
    <Box key={key} maxWidth={290}>
      <MemberChip
        label={item.teamName}
        {...tagProps}
        onDelete={() => handleTeamDelete(item)}
      />
    </Box>
  );
  const renderMemberTag = (item: GroupMember, tagProps: any, key: number) => (
    <MemberChip
      key={key}
      label={item.user.name.full}
      {...tagProps}
      onDelete={() => handleMemberDelete(item.id)}
    />
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <SelectedMemberAvatar dedicatedFor={value ?? []} />
      <Box
        sx={(theme) => ({
          display: "flex",
          width: "100%",
          [theme.breakpoints.down("md")]: { marginBottom: 1 },
        })}
      >
        <Autocomplete
          fullWidth
          multiple={multiple}
          options={visibleOptions}
          value={multiple ? value : value[0] ?? null}
          onChange={handleChange}
          onInputChange={(_, v) => setCurrentInput(v)}
          groupBy={(option) => option.groupName}
          isOptionEqualToValue={(option, value) => {
            if ("isTeam" in option) return false;
            return (option as GroupMember).id === value.id;
          }}
          disableClearable
          autoHighlight={false}
          disableCloseOnSelect={multiple}
          filterOptions={(opts) => opts}
          PopperComponent={StyledPopper}
          ListboxComponent={ListboxComponent}
          ListboxProps={{ onScroll: handleListboxScroll }}
          getOptionLabel={(option) =>
            "isTeam" in option && option.isTeam
              ? option.user.name.full
              : (option as GroupMember).user.name.full
          }
          noOptionsText={t("dialog.form.no-options")}
          renderInput={(params) => {
            const { inputProps } = params;
            return (
              <TextField
                {...params}
                {...props}
                inputProps={{
                  ...inputProps,
                  onKeyDown: (e: React.KeyboardEvent<Element>) => {
                    if (!removeLastTagIfNeeded(e)) {
                      inputProps.onKeyDown?.(e as React.KeyboardEvent<any>);
                    }
                  },
                }}
              />
            );
          }}
          renderTags={(_unused, getTagProps) =>
            displayTags.map((item, index) => {
              const {
                key,
                onDelete: _ignored,
                ...restTagProps
              } = getTagProps({ index });
              if (!("id" in item)) {
                return renderTeamTag(item, restTagProps, key);
              }
              return renderMemberTag(item, restTagProps, key);
            })
          }
          renderOption={(props, option) => {
            let isSelected = false;
            if ("isTeam" in option && option.isTeam) {
              isSelected = selectedTeamIds.includes(option.teamName);
            } else {
              const member = option as GroupMember;
              isSelected = value?.some((m) => m.id === member.id);
            }
            const enhancedProps = { ...props, "aria-selected": isSelected };
            if ("isTeam" in option && option.isTeam) {
              return (
                <MemberMenuItemContent
                  elementProps={enhancedProps}
                  name={option.user.name.full}
                  isTeamOption={true}
                />
              );
            }
            return (
              <MemberMenuItemContent
                elementProps={enhancedProps}
                avatarImageUrl={(option as GroupMember).user.avatarImageUrlSrc}
                name={(option as GroupMember).user.name.full}
                user={(option as GroupMember).user}
              />
            );
          }}
          data-testid="organization-members-autocomplete"
        />
      </Box>
    </Box>
  );
};
